import { assert } from './assertions';
import { intersectCircles, Vec2 } from './vec2';

/**
 * Figure out a valid position for a pattern of atoms, as close as possible to specified point.
 *
 * @param pos the desired origin point where the pattern should be placed
 * @param positions relative positions of atoms in the pattern
 * @param others positions of already present other atoms
 * @param minDistance minimum valid distance between two atoms
 * @param maxNorm maximum distance from any of the "outerCenter" circles
 * @returns found valid positions of atoms in the pattern
 */
export function findClosestValidAtomsPositions(
  searchPos: Vec2,
  positions: Vec2[],
  others: Vec2[],
  minDistance: number,
  maxNorm: number
): Vec2[] {
  // use Minkowski sum of two circle sets to describe
  // many-many collision problem as one-many collision
  const minkowskiSum = positions.flatMap((pos) => {
    return others.map((pos2) => Vec2.sub(pos, pos2));
  });
  const displacement = findClosestValidAtomPosition(
    Vec2.sub(Vec2.ZERO, searchPos),
    minkowskiSum,
    minDistance,
    maxNorm,
    positions
  );
  return positions.map((pos) => Vec2.sub(pos, displacement));
}

/**
 * Figure out a valid position for a new atom, as close as possible to specified point.
 *
 * @param pos the desired position where an atom should be placed
 * @param others positions of already present other points
 * @param minDistance minimum valid distance between two atoms
 * @param maxNorm maximum distance from any of the "outerCenter" circles
 * @param outerCenters list of origins that must be at most `maxNorm` distance from every valid point
 * @returns found valid position
 */
export function findClosestValidAtomPosition(
  pos: Vec2,
  others: Vec2[],
  minDistance: number,
  maxNorm: number,
  outerCenters: Vec2[] = [Vec2.ZERO]
): Vec2 {
  const minDistanceSq = minDistance * minDistance;
  const minDistanceSqTest = minDistance * minDistance - Number.EPSILON * 100;
  const maxNormSq = maxNorm * maxNorm;
  const maxNormCorrection = maxNorm - Number.EPSILON * 100;
  const minDistanceCorrection = minDistance + Number.EPSILON * 100;

  // limit the point by the max radial distance of the device
  for (const center of outerCenters) {
    const normSq = Vec2.distanceSquare(pos, center);
    if (normSq > maxNormSq) {
      const correction = maxNormCorrection / Math.sqrt(normSq);
      pos.x = center.x + (pos.x - center.x) * correction;
      pos.y = center.y + (pos.y - center.y) * correction;
    }
  }

  function pointIsValid(p: Vec2) {
    return (
      outerCenters.every((c) => Vec2.distanceSquare(p, c) <= maxNormSq) &&
      others.every(
        (other) => Vec2.distanceSquare(p, other) >= minDistanceSqTest
      )
    );
  }

  if (pointIsValid(pos)) {
    return pos;
  }

  const intersections = others.filter(
    (p) => Vec2.distanceSquare(pos, p) < minDistanceSq
  );
  // no intersections
  if (intersections.length == 0) {
    return pos;
  }

  // find valid candidate points by moving away from intersections
  const candidatePoints = intersections
    .map((p) => {
      const delta = Vec2.sub(pos, p);
      if (delta.x === 0 && delta.y === 0) {
        // points are equal, add a slight offset to produce some valid candidate
        // delta.x = minDistanceCorrection
        return { x: pos.x + minDistance, y: pos.y };
      }
      const norm = Vec2.norm(delta);
      const displace = (minDistance - norm) / norm;
      const newPoint = {
        x: pos.x + delta.x * displace,
        y: pos.y + delta.y * displace
      };

      // double check distance of calculated point
      assert(
        Vec2.distanceSquare(newPoint, p) >= minDistanceSqTest,
        'calculated candidate too close'
      );
      return newPoint;
    })
    // make sure the candidate point is valid
    .filter(pointIsValid);

  if (candidatePoints.length > 0) {
    // find closest candidate point
    let closestPoint = candidatePoints[0];
    let closestDistSq = Vec2.distanceSquare(pos, closestPoint);
    for (let i = 1; i < candidatePoints.length; i++) {
      const candidate = candidatePoints[i];
      const distSq = Vec2.distanceSquare(candidate, pos);
      if (distSq < closestDistSq) {
        closestPoint = candidate;
        closestDistSq = distSq;
      }
    }
    return closestPoint;
  }

  // complex case. Find valid circle intersection points as candidates,
  // then find the closest one.
  let closestPoint = pos;
  let closestDistSq = Number.POSITIVE_INFINITY;
  for (let i = 0; i < others.length; i++) {
    const p = others[i];
    for (let j = i + 1; j < others.length; j++) {
      const intersections = intersectCircles(
        p,
        minDistanceCorrection,
        others[j],
        minDistanceCorrection
      );
      if (intersections != null) {
        for (const candidate of intersections) {
          if (pointIsValid(candidate)) {
            const distSq = Vec2.distanceSquare(pos, candidate);
            if (distSq < closestDistSq) {
              closestPoint = candidate;
              closestDistSq = distSq;
            }
          }
        }
      }
    }
    // find intersection with outer circles
    for (let i = 0; i < outerCenters.length; i++) {
      const borderIntersections = intersectCircles(
        p,
        minDistanceCorrection,
        outerCenters[i],
        maxNormCorrection
      );
      if (borderIntersections != null) {
        for (const candidate of borderIntersections) {
          if (pointIsValid(candidate)) {
            const distSq = Vec2.distanceSquare(pos, candidate);
            if (distSq < closestDistSq) {
              closestPoint = candidate;
              closestDistSq = distSq;
            }
          }
        }
      }
    }
  }

  // if all failed, we just return the original pos
  return closestPoint;
}

/** Find the min and max distance from the center for a set of atoms */
export function getAtomsMagExteremes(atoms: Vec2[]) {
  let shortestMag = 0;
  let largestMag = 0;
  for (const atom of atoms) {
    const mag = Vec2.norm(atom);
    if ((mag !== 0 && mag < shortestMag) || shortestMag === 0) {
      shortestMag = mag;
    }
    if (mag !== 0 && mag > largestMag) {
      largestMag = mag;
    }
  }

  return {
    shortestMag,
    largestMag
  };
}

/**
 * Compute the bounding box of a set of atoms
 */
export function getBoundingBox(positions: Vec2[]) {
  let bounds: { min: Vec2; max: Vec2 } | null = null;
  for (const pos of positions) {
    if (bounds == null) {
      bounds = { min: pos, max: pos };
    } else {
      bounds.min = Vec2.min(bounds.min, pos);
      bounds.max = Vec2.max(bounds.max, pos);
    }
  }
  return bounds ?? { min: Vec2.ZERO, max: Vec2.ZERO };
}

/**
 * Compute the valid connections between atoms
 */
export function getConnections(
  positions: Vec2[],
  rydbergRadius: number,
  c6Coefficient: number
) {
  const connections: {
    a: Vec2;
    b: Vec2;
    width: number;
    vanderWaalsStrength: number;
  }[] = [];
  const rydbergRadiusSq = rydbergRadius * rydbergRadius;
  const halfc6Coefficient = c6Coefficient / 2;

  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i];
    for (let j = i + 1; j < positions.length; j++) {
      const otherPos = positions[j];
      const distSq = Vec2.distanceSquare(pos, otherPos);
      if (distSq < rydbergRadiusSq) {
        const falloff = (rydbergRadiusSq - distSq) / rydbergRadiusSq;
        const dist6 = distSq * distSq * distSq;
        const vanderWaalsStrength = halfc6Coefficient / dist6;

        connections.push({
          a: pos,
          b: otherPos,
          width: 2 + falloff * 5,
          vanderWaalsStrength
        });
      }
    }
  }
  return connections;
}
